import api from '@/store/api'

import { AIMessage } from './types'
import { WSResponse } from '@/services/ws/utils/types'

type AIAgentMessagesApiResponse = {
  thread_id: string
  messages: AIMessage[]
  message_count: number
}

export const fetchThread = async (threadId: string) => {
  try {
    const result = await api.get<AIAgentMessagesApiResponse>(
      `ai_conversations/${threadId}`,
    )
    return result.data.messages
  } catch (e) {
    throw Error(e as string)
  }
}

export const destroyTread = async (threadId: string) => {
  try {
    await api.delete(
      `ai_conversations/${threadId}`,
    )
  } catch (e) {
    throw Error(e as string)
  }
}

/**
 * The request a suggestion assistant is answering, when the user typed nothing.
 *
 * The wizard can be opened straight from the Add asset dialog, and its two
 * suggestion assistants are still worth asking then — what usually owns a
 * property, how this repository files a fund. But every one of them takes the
 * user's own words as its request, and an empty message is not a question.
 *
 * So the absence is stated rather than sent as a blank: the prompts branch on
 * this line, and answer from the flow and the repository instead of pretending
 * to have read something. The wording is load-bearing — see
 * `ai_tag_suggestion` and `ai_account_type_suggestion`.
 */
export const noSentenceRequest = (flow: string) =>
  `No description given — the user opened the "${flow}" wizard directly and is choosing this themselves.`

export const prepareMessages = (content: string) => {
  const messages: AIMessage[] = []
  const message: AIMessage = {
    role: 'human',
    content,
  }
  messages.push(message)
  return { messages, message }
}

export const handleResponse = (
  requestId: string,
  currentThreadId: string,
  message: WSResponse<string>,
  setErrorMessage: (error: string) => void,
  setCurrentThreadId: (threadId: string) => void,
  pushMessage: (message: AIMessage) => void,
  setConversationLastUpdated?: (threadId: string) => void,
) => {
  try {
    if (message.errors) {
      const errors = message.errors.join(', ')
      setErrorMessage(errors)
      return
    }
    if (!message.data || requestId !== currentThreadId) return
    if (message.thread_id) {
      setCurrentThreadId(message.thread_id)
      setConversationLastUpdated?.(message.thread_id)
    }
    pushMessage({
      role: 'ai',
      content: message.data,
    })
  } catch (e) {
    throw Error(e as string)
  }
}
